import { useTheme } from '../../theme';

export interface LoadingModalProps {
  /** Controla visibilidade do modal */
  open: boolean;
  /** Mensagem exibida abaixo do spinner (ex.: "Guardando borrador...") */
  message?: string;
}

/**
 * Modal de loading com overlay e card no estilo dos modais de Create Course.
 * Usa as mesmas cores e estilos (createCourse.modalOverlay, modalShadow, background.paper).
 */
export function LoadingModal({ open, message }: LoadingModalProps) {
  const theme = useTheme();

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: theme.palette.createCourse.modalOverlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: theme.spacing.lg,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.lg,
    boxShadow: theme.palette.createCourse.modalShadow,
    padding: theme.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.lg,
    minWidth: 200,
  };

  const spinnerSize = 40;
  const spinnerBorderWidth = 3;
  const spinnerStyle: React.CSSProperties = {
    width: spinnerSize,
    height: spinnerSize,
    borderRadius: '50%',
    border: `${spinnerBorderWidth}px solid ${theme.palette.border.main}`,
    borderTopColor: theme.palette.primary.main,
    animation: 'LoadingModal-spin 0.8s linear infinite',
    flexShrink: 0,
  };

  const messageStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.text.secondary,
    margin: 0,
    textAlign: 'center',
  };

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={message ?? undefined}
      style={overlayStyle}
    >
      <style>{`
        @keyframes LoadingModal-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={modalStyle}>
        <div style={spinnerStyle} aria-hidden />
        {message != null && message !== '' && (
          <p style={messageStyle}>{message}</p>
        )}
      </div>
    </div>
  );
}
